import { useSelector, useDispatch } from "react-redux";
import { COLUMN_CONFIGS, DRM_COLUMN } from "../../constData/columnDrmt";
import { getDrmTableData } from "../../redux/reducers/drmTableDataSlice";
import { setDrmTableFilter } from "../../redux/reducers/drmTableFilterSlice";
import { setDrmTableFilter as setDrmDailyTableFilter } from "../../redux/reducers/drmDailyTableFilterSlice";
import { setCoupaTableFilter } from "../../redux/reducers/drmCoupaTableFilterSlice";
import { setCeaProfileFilter } from "../../redux/reducers/drmCeaProfileTableFilterSlice";
import { STATUSCODE } from "../../helpers";
import { getDrmChangeLogTableData } from "../../redux/reducers/drmChangeLogTableDataSlice";
import { getCoupaFilesDeleteDetails } from "../../redux/reducers/drmCoupaFIleDeleteDetailsSlice";
import { getCeaProfileList } from "../../redux/reducers/drmCeaProfileListSlice";
import { useLocation } from "react-router-dom";

const MENU_TYPES = {
  BID: "bid_maintenance",
  DAILY: "daily_maintenance",
  COUPA: "coupa_file_management",
  CEA_PROFILE: "cea_profile_download",
};

const TABLE_NAMES = {
  [MENU_TYPES.DAILY]: "drmDailyTableData",
  [MENU_TYPES.BID]: "drmBidTableData",
  [MENU_TYPES.CEA_PROFILE]: "drmCeaProfileList",
};

export const DropdownColumnFilter = ({ column }) => {
  const { filterValue, Header } = column;
  const config = COLUMN_CONFIGS[Header];

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dispatch = useDispatch();
  const process = searchParams.get("process");
  const { tableFilter } = useSelector((state) => state.drmFilterPayload);
  const { dailyTableFilter } = useSelector((state) => state.drmDailyFilterPayload);
  const { coupaTableFilter } = useSelector((state) => state.drmCoupaFilterPayload);
  const { ceaProfileFilter } = useSelector((state) => state.drmCeaProfileFilterPayload);
  const { selectedMenu } = useSelector((state) => state.selectedMenu);

  if (!config?.filterComponent) return null;

  const isCoupaPage = location?.pathname.includes(MENU_TYPES.COUPA);
  const isCeaProfilePage = location?.pathname.includes(MENU_TYPES.CEA_PROFILE);
  const isBidMaintenance = !isCoupaPage && !isCeaProfilePage && selectedMenu === MENU_TYPES.BID;
  let currentFilter = dailyTableFilter;
  let setFilterAction = setDrmDailyTableFilter;

  if (isCeaProfilePage) {
    currentFilter = ceaProfileFilter;
    setFilterAction = setCeaProfileFilter;
  } else if (isCoupaPage) {
    currentFilter = coupaTableFilter;
    setFilterAction = setCoupaTableFilter;
  } else if (isBidMaintenance) {
    currentFilter = tableFilter;
    setFilterAction = setDrmTableFilter;
  }

  const getAccessor = () => {
    const accessor = DRM_COLUMN.find((item) => item.Header === Header)?.accessor;
    return accessor === "message" ? "status" : Header;
  };

  const convertStatusValue = (value, accessor) => {
    if (accessor !== "status") {
      return Array.isArray(value) ? (value.length > 0 ? value : filterValue) : value || filterValue;
    }

    if (Array.isArray(value)) {
      return value
        .map((val) => STATUSCODE.find((n) => n.message === val)?.code)
        .filter((code) => code !== undefined);
    }

    return STATUSCODE.find((n) => n.message === value)?.code;
  };

  const getDisplayValue = (value, originalAccessor) => {
    if (originalAccessor !== "message" || !value) return value;

    if (Array.isArray(value)) {
      return value
        .map((code) => STATUSCODE.find((status) => status.code === code)?.message)
        .filter((message) => message !== undefined);
    }

    return STATUSCODE.find((status) => status.code === value)?.message || value;
  };

  const COMPLEX_FILTER_FIELDS = {
    miles: { params: [], operator: "" },
    rate: { params: [], operator: "" },
    btfRate: { params: [], operator: "" },
    lineHaulRate: { params: [], operator: "" },
    effectiveDate: { date: [], operator: "" },
    expirationDate: { date: [], operator: "" },
    qtyStartDate: { date: [], operator: "" },
    qtyEndDate: { date: [], operator: "" },
    pctStartDate: { date: [], operator: "" },
    pctEndDate: { date: [], operator: "" },
    minShipmentsAllocated: { params: [], operator: "" },
    maxShipmentsAllocated: { params: [], operator: "" },
    pctAward: { params: [], operator: "" },
    minCarrierAllocationPercentage: { params: [], operator: "" },
    maxCarrierAllocationPercentage: { params: [], operator: "" },
    createdDate: { date: [], operator: "" },
    updatedDate: { date: [], operator: "" },
  };

  const handleFilterSet = (newValue) => {
    const accessor = getAccessor();
    if (!accessor) return;

    const value = convertStatusValue(newValue, accessor);
    
    let filterValue;
    if (COMPLEX_FILTER_FIELDS[accessor]) {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        filterValue = {
          ...COMPLEX_FILTER_FIELDS[accessor],
          ...value
        };
      } else {
        const currentComplexValue = currentFilter.filters[accessor] || COMPLEX_FILTER_FIELDS[accessor];
        
        if (currentComplexValue.hasOwnProperty('params')) {
          filterValue = {
            ...currentComplexValue,
            params: Array.isArray(value) ? value : value ? [value] : []
          };
        } else if (currentComplexValue.hasOwnProperty('date')) {
          filterValue = {
            ...currentComplexValue,
            date: Array.isArray(value) ? value : value ? [value] : []
          };
        } else {
          filterValue = currentComplexValue;
        }
      }
    } else {
      filterValue = value || [];
    }

    const updatedFilter = {
      ...currentFilter,
      filters: {
        ...currentFilter.filters,
        [accessor]: filterValue,
      },
    };

    dispatch(setFilterAction(updatedFilter));
    handleFilterAPICall(updatedFilter);
  };

  const handleFilterAPICall = (updatedFilter) => {
    const { current, ...rest } = updatedFilter;

    let selectedRoute = null;
    if (location?.pathname.includes(MENU_TYPES.CEA_PROFILE)) {
      selectedRoute = MENU_TYPES.CEA_PROFILE;
    } else if (location?.pathname.includes(MENU_TYPES.DAILY)) {
      selectedRoute = MENU_TYPES.DAILY;
    } else if (location?.pathname.includes(MENU_TYPES.BID)) {
      selectedRoute = MENU_TYPES.BID;
    } else if (location?.pathname.includes(MENU_TYPES.COUPA)) {
      selectedRoute = MENU_TYPES.COUPA;
    }

    const filters = {
      process,
      filters: { ...rest.filters }
    };

    if (selectedRoute === MENU_TYPES.CEA_PROFILE) {
      const normalizedCeaProfileFilter = {
        ...rest,
        filters: {
          ...(rest.filters || {}),
          isDownloaded: rest?.filters?.isDownloaded,
        },
      };

      dispatch(
        getCeaProfileList({
          reqBody: normalizedCeaProfileFilter,
          pageSize: 30,
          pageNum: 1,
          tableName: TABLE_NAMES[MENU_TYPES.CEA_PROFILE],
        })
      );
      return;
    }

    if (selectedRoute === MENU_TYPES.COUPA) {
      dispatch(
        getCoupaFilesDeleteDetails({
          reqBody: rest,
          pageSize: 30,
          pageNum: 1,
          tableName: "drmCoupaFileDeleteDetails",
        })
      );
      return;
    }

    if (selectedRoute) {
      dispatch(
        getDrmTableData({
          reqBody: rest,
          pageSize: 30,
          pageNum: 1,
          tableName: TABLE_NAMES[selectedRoute] || TABLE_NAMES[MENU_TYPES.BID],
        })
      );
    } else {
      dispatch(
        getDrmChangeLogTableData({
          pageSize: 30,
          pageNum: 1,
          tableName: 'drmChangeLogTableData',
          reqBody: filters
        })
      );
    }
  };

  const accessor = getAccessor();
  const storedFilterValue = accessor && currentFilter?.filters?.[accessor]
    ? currentFilter.filters[accessor]
    : filterValue;

  const originalAccessor = DRM_COLUMN.find((item) => item.Header === Header)?.accessor;
  const displayFilterValue = getDisplayValue(storedFilterValue, originalAccessor);

  const FilterComponent = config.filterComponent;
  return (
    <FilterComponent
      value={displayFilterValue}
      header={Header}
      handleFilterSet={handleFilterSet}
    />
  );
};